import Panel from "@/components/Panel";
import {useAppState} from "@/store";
import PianoIcon from "@/components/icons/PianoIcon";
import SafeIcon from "@/components/icons/SafeIcon";
import ArtIcon from "@/components/icons/ArtIcon";
import PoolTableIcon from "@/components/icons/PoolTableIcon";
import ClockIcon from "@/components/icons/ClockIcon";
import NextButton from "@/components/NextButton";
import CartIcon from "@/components/icons/CartIcon";
import StepTitles from "@/components/StepTitles";
import {useValidation} from "@/validation";
import StepFooter from "@/components/StepFooter";
import {API_ENDPOINTS} from "@/constants";
import PackingChoice from "@/components/fields/PackingChoice";
import {useApi} from "@/api";
import {apiUrl, dataLayerPush} from "@/helpers";
import {useEffect} from "react";
import ChoicePair from "@/components/fields/ChoicePair";
import {toast} from "react-toastify";
import ChoiceMulti from "@/components/fields/ChoiceMulti";

export default function SpecialItems({goToStep}) {

    const {state, setState} = useAppState()
    const {api, setLoading} = useApi()
    const {preSubmitValidate} = useValidation()

    const handleSubmit = async () => {

        setLoading(true)

        try {

            preSubmitValidate()

            const postData = {
                "requestId": state.requestId,
                "sameDay": state.special.same_day,
                "everything": state.special.everything,
                "packing": state.special.packing,
                "items": state.special.items,
                "multipleStops": state.special.multiple_stops,
            }

            if (state.special.everything === 'no') {
                postData['moveStory'] = state.special.move_story

                if (state.special.move_story === 'furniture' || state.special.move_story === 'college' || state.special.move_story === 'downsizing') {
                    postData['moveStoryAmount'] = state.special.move_story_amount
                }

                if (state.special.move_story === 'storage') {
                    postData['moveStoryStorageSize'] = state.special.move_story_storage_size
                }
            }

            if (state.special.packing === 'yes') {
                postData['packingRooms'] = state.special.packing_rooms
            }

            if (state.special.multiple_stops === 'yes') {
                postData['numStops'] = state.special.num_stops
            }

            const response = await api.post(apiUrl(API_ENDPOINTS.items), postData)

            setState((s) => {
                s.inestimable['items'] = response.inestimable?.status  ? response.inestimable?.reasons : false
            })

            await dataLayerPush('book-special-submit', {
                ['book-special-moving-24h']: state.special.same_day,
                ['book-special-move-everything']: state.special.everything,
                ['book-special-packing']: state.special.packing,
                ['book-special-items']: state.special.items,
                ['book-special-multiple']: state.special.multiple_stops,
                ['book-assigned-branch']: state.branch?.name || null
            })

            goToStep(3)
        } catch (e) {
            toast.error(e.message)
        }

        setLoading(false)
    }

    const handleCheckboxChange = (key, {value, checked}) => {
        setState((s) => {
            if (!s.special[key]) {
                s.special[key] = []
            }

            if (checked) {
                s.special[key].push(value)
            } else {
                s.special[key] = s.special[key].filter(i => i !== value)
            }
        })
    }

    useEffect(() => {
        dataLayerPush('book-special-view', {
            ['book-assigned-branch']: state.branch?.name || null
        }).then()
    }, [])

    const content = window.ein?.content?.step_2 || {}

    const {title, secondary_title, tertiary_title, button_label} = content

    return (
        <div className={"grid grid-cols-4 lg:grid-cols-12 gap-x-[20px] lg:gap-x-[32px] gap-y-[40px]"}>
            <div className="col-span-4 lg:col-span-12 flex flex-col items-center text-center">
                <CartIcon className={'size-[80px] mb-[16px]'}/>
                <StepTitles title={title} secondaryTitle={secondary_title} tertiaryTitle={tertiary_title}/>
            </div>
            <div className="col-span-4 lg:col-span-8 lg:col-start-3">
                <div className={"flex flex-col gap-[32px] lg:gap-[40px]"}>
                    <Panel passive={true}>
                        <div className="heading-h4 text-center">
                            Is your move happening in the next 24 hours?
                        </div>
                        <ChoicePair
                            name={"same_day"}
                            labels={[
                                <><strong>Yes</strong></>,
                                <><strong>No</strong></>
                            ]}
                            values={[
                                "yes",
                                "no"
                            ]}
                        />
                    </Panel>
                    <Panel passive={true}>
                        <div className="heading-h4 text-center">
                            Are you moving everything from one property to the other?
                        </div>
                        <ChoicePair
                            name={"everything"}
                            labels={[
                                <><strong>Yes</strong>, the whole lot! 🏠</>,
                                <><strong>No</strong>, just some stuff 📦</>
                            ]}
                            values={[
                                "yes",
                                "no"
                            ]}
                        />

                        {state.special.everything === 'no' && (
                            <div className={"mt-[24px]"}>
                                <div className="heading-h4 text-center">
                                    Got it! What's the story about this move?
                                </div>
                                <div className={"text-center italic text-brand-grey text-sm mt-[4px]"}>
                                    This helps us get you a real number, rather than bumping you to a phone call. 👇
                                </div>

                                <ChoiceMulti
                                    stateKey={"special"}
                                    name={"move_story"}
                                    labels={[
                                        <>Just some furniture</>,
                                        <>Moving to / from storage</>,
                                        <>Kid heading to college</>,
                                        <>Downsizing / splitting a home</>,
                                        <>Something else</>
                                    ]}
                                    values={[
                                        "furniture",
                                        "storage",
                                        "college",
                                        "downsizing",
                                        "other"
                                    ]}
                                />

                                {(
                                    state.special.move_story
                                    && (state.special.move_story === 'furniture' || state.special.move_story === 'college' || state.special.move_story === 'downsizing'))
                                    && (
                                    <div className={"mt-[24px]"}>
                                        <div className="heading-h4 text-center">
                                            About how much are we moving?
                                        </div>
                                        <div className={"text-center italic text-brand-grey text-sm mt-[4px]"}>
                                            No need to measure — just ballpark it.
                                        </div>

                                        <ChoiceMulti
                                            stateKey={"special"}
                                            name={"move_story_amount"}
                                            labels={[
                                                <>A few pieces</>,
                                                <>About a 1-bedroom's worth</>,
                                                <>About a 2-bedroom's worth</>,
                                                <>3+ bedrooms worth</>
                                            ]}
                                            values={[
                                                "few",
                                                "1_bedroom",
                                                "2_bedroom",
                                                "3_plus_bedroom"
                                            ]}
                                        />
                                    </div>
                                )}

                                {(state.special.move_story && state.special.move_story === 'storage') && (
                                    <div className={"mt-[24px]"}>
                                        <div className="heading-h4 text-center">
                                            What size is your storage unit?
                                        </div>
                                        <div className={"text-center italic text-brand-grey text-sm mt-[4px]"}>
                                            We'll base your quote on your storage size, not your whole home.
                                        </div>

                                        <ChoiceMulti
                                            stateKey={"special"}
                                            name={"move_story_storage_size"}
                                            labels={[
                                                <>5x5</>,
                                                <>5x10</>,
                                                <>10x10</>,
                                                <>10x15</>,
                                                <>10x20</>
                                            ]}
                                            values={[
                                                "5x5",
                                                "5x10",
                                                "10x10",
                                                "10x15",
                                                "10x20"
                                            ]}
                                        />
                                    </div>
                                )}

                            </div>
                        )}

                    </Panel>
                    <Panel passive={true}>
                        <div className="heading-h4 text-center">
                            Do you need help packing your belongings into boxes?
                        </div>
                        <div className={"text-center italic text-brand-grey text-sm mt-[4px]"}>
                            Wrapping and padding your furniture is always included—this is just about getting your stuff into boxes.
                        </div>
                        <ChoicePair
                            name={"packing"}
                            labels={[
                                <><strong>Yes</strong>, help me please! 🙏</>,
                                <><strong>No</strong>, I’ll do it myself 💪</>
                            ]}
                            values={[
                                "yes",
                                "no"
                            ]}
                        />

                        {state.special.packing === 'yes' && (
                            <div className={"mt-[24px]"}>
                                <div className="heading-h4 text-center">
                                    Which rooms need packing?
                                </div>
                                <div className={"text-center italic text-brand-grey text-sm mt-[4px]"}>
                                    Lets us scope the packing instead of guessing. Tap all that apply, or "the whole house" if it's everything.
                                </div>

                                <ChoiceMulti
                                    allowMultiple={true}
                                    stateKey={"special"}
                                    name={"packing_rooms"}
                                    labels={[
                                        <>The whole house 🏠</>,
                                        <>Kitchen</>,
                                        <>Bedrooms</>,
                                        <>Garage</>,
                                        <>Living room</>,
                                        <>Office</>,
                                        <>Other</>
                                    ]}
                                    values={[
                                        "whole",
                                        "kitchen",
                                        "bedrooms",
                                        "garage",
                                        "living_room",
                                        "office",
                                        "other"
                                    ]}
                                />
                            </div>
                        )}

                    </Panel>
                    <Panel passive={true}>
                        <div className="heading-h4 text-center">
                            Are you making any additional stops?
                        </div>
                        <ChoicePair
                            name={"multiple_stops"}
                            labels={[
                                <><strong>Yes</strong>, we're doing a crawl 🚌</>,
                                <><strong>No</strong>, just here to there 📍</>
                            ]}
                            values={[
                                "yes",
                                "no"
                            ]}
                        />

                        {state.special.multiple_stops === 'yes' && (
                            <div className={"mt-[24px]"}>
                                <div className="heading-h4 text-center">
                                    How many stops total?
                                </div>
                                <div className={"text-center italic text-brand-grey text-sm mt-[4px]"}>
                                    A quick count lets us price the route instead of bumping you to a call.
                                </div>

                                <ChoiceMulti
                                    stateKey={"special"}
                                    name={"num_stops"}
                                    labels={[
                                        <>1 stop</>,
                                        <>2 stops</>,
                                        <>3+ stops</>
                                    ]}
                                    values={[
                                        "1_stop",
                                        "2_stops",
                                        "3_plus_stops"
                                    ]}
                                />
                            </div>
                        )}

                    </Panel>
                    <Panel passive={true}>
                        <div className="heading-h4 text-center">
                            Are any of these special items making the journey?
                        </div>
                        <div className={"mt-[24px] grid grid-cols-2 md:flex md:flex-wrap gap-[20px] md:gap-[32px] md:justify-center"}>
                            <PackingChoice
                                name={"piano"}
                                icon={<PianoIcon className={"size-[70px]"}/>}
                                label={"Piano"}
                                onChange={target => handleCheckboxChange('items', target)}
                            />
                            <PackingChoice
                                name={"safe"}
                                icon={<SafeIcon className={"size-[70px]"}/>}
                                label={`Safe<br/>(over 600lbs)`}
                                onChange={target => handleCheckboxChange('items', target)}
                            />
                            <PackingChoice
                                name={"artwork"}
                                icon={<ArtIcon className={"size-[70px]"}/>}
                                label={`Artwork<br/>(worth over $2,000)`}
                                onChange={target => handleCheckboxChange('items', target)}
                            />
                            <PackingChoice
                                name={"pool_table"}
                                icon={<PoolTableIcon className={"size-[70px]"}/>}
                                label={`Pool Table`}
                                onChange={target => handleCheckboxChange('items', target)}
                            />
                            <PackingChoice
                                name={"heavy_furniture"}
                                icon={<ClockIcon className={"size-[70px]"}/>}
                                label={`Heavy Furniture<br/>(over 600lbs)`}
                                onChange={target => handleCheckboxChange('items', target)}
                            />
                        </div>
                    </Panel>
                </div>
            </div>
            <div className="col-span-4 lg:col-span-12 flex justify-center">
                <NextButton onClick={handleSubmit} text={button_label} disabled={false} />
            </div>
            <div className="col-span-4 lg:col-span-12">
                <StepFooter/>
            </div>
        </div>
    )
}
